import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'

// Module-level cache for liked status, keyed by `${userId}:${type}:${id}`.
// Lives for the lifetime of the tab. Navigating between Browse, Home, etc.
// re-mounts StoryCards but we reuse the cached value instead of hitting
// /api/stories/:id/like for every card every time.
//
// Cache writes: on first successful fetch, and on every toggle (so subsequent
// mounts reflect the optimistic change immediately without a refetch).
const likedCache = new Map()
const cacheKey = (userId, type, id) => `${userId}:${type}:${id}`

export function useLike(type, id, initialLikes, initialLiked) {
  const { user, getToken } = useAuth()
  const cached = user && id ? likedCache.get(cacheKey(user.id, type, id)) : undefined
  // Consumption order on initial state: cache wins (reflects user's latest
  // toggle), then initialLiked from server payload, then false.
  const [liked, setLiked] = useState(
    cached ?? (typeof initialLiked === 'boolean' ? initialLiked : false)
  )
  const [count, setCount] = useState(initialLikes ?? 0)
  const [busy,  setBusy]  = useState(false)
  // Track whether the user has interacted so we don't overwrite with stale server data
  const userActed = useRef(false)

  // Only sync count from parent if the user hasn't liked/unliked yet this session
  useEffect(() => {
    if (!userActed.current) setCount(initialLikes ?? 0)
  }, [initialLikes])

  // Load real liked status from server on mount, with this priority:
  //   1. likedCache (existing module-level Map) — reflects optimistic toggles
  //   2. initialLiked from server payload (e.g. story.likedByMe) — skip fetch
  //   3. fetch /api/<base>/<id>/like as fallback
  // Order is load-bearing: cache must win over initialLiked because the cache
  // reflects a toggle the user just performed; initialLiked from a refetched
  // list could be stale relative to that optimistic toggle.
  useEffect(() => {
    if (!user || !id) return
    const key = cacheKey(user.id, type, id)
    if (likedCache.has(key)) {
      setLiked(likedCache.get(key))
      return
    }
    if (typeof initialLiked === 'boolean') {
      likedCache.set(key, initialLiked)
      setLiked(initialLiked)
      return
    }
    const base = type === 'quiz' ? 'quizzes' : 'stories'
    getToken().then(token => {
      if (!token) return
      axios.get(`/api/${base}/${id}/like`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(({ data }) => {
          const next = !!data.liked
          likedCache.set(key, next)
          setLiked(next)
        })
        .catch(err => console.error('[useLike] Failed to fetch like status:', err))
    })
  }, [user?.id, id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function toggle(e) {
    e.preventDefault()
    e.stopPropagation()
    if (busy) return
    if (navigator.vibrate) navigator.vibrate(10)
    setBusy(true)
    userActed.current = true

    const wasLiked = liked
    const wasCount = count

    // Optimistic update + cache write so other cards / future mounts see it.
    setLiked(!wasLiked)
    setCount(wasLiked ? Math.max(0, wasCount - 1) : wasCount + 1)
    if (user && id) likedCache.set(cacheKey(user.id, type, id), !wasLiked)

    try {
      const token = await getToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const base = type === 'quiz' ? 'quizzes' : 'stories'
      const url = `/api/${base}/${id}/like`
      if (wasLiked) {
        const { data } = await axios.delete(url, { headers })
        if (data?.likes !== undefined) setCount(data.likes)
      } else {
        const { data } = await axios.post(url, {}, { headers })
        if (data?.likes !== undefined) setCount(data.likes)
      }
    } catch {
      // Revert state + cache on error
      setLiked(wasLiked)
      setCount(wasCount)
      if (user && id) likedCache.set(cacheKey(user.id, type, id), wasLiked)
    } finally {
      setBusy(false)
    }
  }

  return { liked, count, toggle }
}
